import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  Motorcycle,
  BatteryHigh,
  CheckCircle,
  Clock,
  Wrench,
  User,
  CalendarBlank,
  ArrowLeft,
  QrCode,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const CHECKLIST_LABELS = {
  motor_instalado: "Motor instalado",
  freios_verificados: "Freios verificados",
  luzes_funcionando: "Luzes funcionando",
  painel_conectado: "Painel conectado",
  acelerador_testado: "Acelerador testado",
  pneus_calibrados: "Pneus calibrados",
  bateria_fixada: "Baterias fixadas",
  carenagem_fechada: "Carenagem fechada",
  bateria_conectada: "Bateria conectada",
  freios_ok: "Freios OK",
  luzes_testadas: "Luzes testadas",
  painel_digital: "Painel digital",
};

const formatChecklistLabel = (key) => {
  if (CHECKLIST_LABELS[key]) return CHECKLIST_LABELS[key];
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
};

const ACTION_ICONS = {
  motorcycle_created: "🏍️",
  battery_linked: "🔋",
  battery_unlinked: "❌",
  checklist_updated: "✅",
  motorcycle_completed: "🎉",
  user_register: "👤",
  observation_added: "📝",
  revision_started: "🔧",
  revision_checklist_updated: "🔧",
  revision_completed: "✅",
};

const OBS_TYPE_CONFIG = {
  observation: { label: "Observação", color: "bg-blue-500/10 text-blue-500" },
  issue: { label: "Avaria", color: "bg-[#FF3B30]/10 text-[#FF3B30]" },
  repair: { label: "Reparo", color: "bg-yellow-500/10 text-yellow-500" },
};

export default function TrackingPage() {
  const { chassisSN } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${API}/tracking/chassis/${encodeURIComponent(chassisSN)}`,
        );
        setData(response.data);
      } catch (err) {
        if (err.response?.status === 404) {
          setError("Moto não encontrada");
        } else {
          setError("Erro ao carregar dados");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [chassisSN]);

  const getStatusText = (status) => {
    switch (status) {
      case "completed":
        return "Finalizada";
      case "assembly":
        return "Em Montagem";
      case "revision":
        return "Em Revisão";
      case "revised":
        return "Revisão Concluída";
      default:
        return "Pendente";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "bg-[#00E676]";
      case "assembly":
        return "bg-yellow-500";
      case "revision":
        return "bg-orange-500";
      case "revised":
        return "bg-blue-500";
      default:
        return "bg-[#A1A1AA]";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#00E676] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center p-4">
        <div className="card p-8 text-center max-w-sm w-full">
          <Motorcycle size={64} className="mx-auto text-[#A1A1AA] mb-4" />
          <h1 className="text-xl font-bold mb-2">Moto Não Encontrada</h1>
          <p className="text-[#A1A1AA] mb-6">
            Não foi possível encontrar uma moto com o chassi informado.
          </p>
          <Link
            to="/"
            className="btn-primary w-full flex items-center justify-center"
          >
            Voltar ao Início
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Header */}
      <header className="bg-[#121212] border-b border-white/10 p-4 sticky top-0 z-10">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-[#00E676] rounded-lg flex items-center justify-center flex-shrink-0">
              <QrCode size={24} className="text-black" />
            </div>
            <div className="min-w-0">
              <h1 className="font-bold text-sm truncate">Rastreio de Moto</h1>
              <p className="font-mono text-xs text-[#A1A1AA] truncate">
                {chassisSN}
              </p>
            </div>
          </div>
          <Link
            to="/login"
            className="text-sm text-[#00E676] hover:text-[#00C853] transition-colors font-medium whitespace-nowrap ml-4"
            data-testid="login-link"
          >
            Acessar Sistema
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="p-4 space-y-4 pb-12">
        {/* Status Card */}
        <div className="card p-5">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-[#1A1A1A] flex items-center justify-center flex-shrink-0">
              <Motorcycle
                size={32}
                className="text-[#A1A1AA] sm:w-10 sm:h-10"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1.5">
                <span
                  className={`w-2 h-2 rounded-full ${getStatusColor(data.status)} shrink-0`}
                ></span>
                <span className="text-xs sm:text-sm font-bold tracking-wide uppercase">
                  {getStatusText(data.status)}
                </span>
              </div>
              <h2 className="text-lg sm:text-xl font-bold truncate">
                {data.model}
              </h2>
              <p className="font-mono text-xs sm:text-sm text-[#A1A1AA] truncate">
                {data.chassis_sn}
              </p>
            </div>
            <div className="text-right flex-shrink-0">
              <p className="text-xl sm:text-2xl font-bold">{data.voltage}V</p>
            </div>
          </div>
        </div>

        {/* Batteries */}
        <div className="card p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <BatteryHigh size={20} className="text-[#00E676]" />
              <h3 className="font-bold text-sm sm:text-base">
                Baterias Vinculadas
              </h3>
            </div>
            <span className="text-sm font-mono text-[#A1A1AA]">
              {data.batteries.length}/{data.voltage === 60 ? 5 : 6}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {data.batteries.map((bat, idx) => (
              <div
                key={idx}
                className="bg-[#1A1A1A] p-2.5 sm:p-3 rounded-lg flex items-center gap-2 border border-white/5"
              >
                <CheckCircle
                  size={16}
                  weight="fill"
                  className="text-[#00E676] shrink-0"
                />
                <span className="font-mono text-xs sm:text-sm break-all">
                  {bat}
                </span>
              </div>
            ))}
            {data.batteries.length === 0 && (
              <p className="text-[#A1A1AA] text-sm col-span-full py-2">
                Nenhuma bateria vinculada
              </p>
            )}
          </div>
        </div>

        {/* Checklist */}
        <div className="card p-5">
          <h3 className="font-bold mb-4 text-sm sm:text-base">
            Checklist de Montagem
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {Object.entries(data.checklist).map(([key, checked]) => (
              <div
                key={key}
                className={`p-2.5 sm:p-3 rounded-lg flex items-center gap-3 border border-transparent transition-colors ${
                  checked
                    ? "bg-[#00E676]/10 border-[#00E676]/20"
                    : "bg-[#1A1A1A]"
                }`}
              >
                {checked ? (
                  <CheckCircle
                    size={18}
                    weight="fill"
                    className="text-[#00E676] shrink-0"
                  />
                ) : (
                  <Clock size={18} className="text-[#A1A1AA] shrink-0" />
                )}
                <span
                  className={`text-xs sm:text-sm leading-snug ${checked ? "text-white font-medium" : "text-[#A1A1AA]"}`}
                >
                  {formatChecklistLabel(key)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Dates */}
        <div className="card p-5">
          <h3 className="font-bold mb-4 text-sm sm:text-base">
            Informações Gerais
          </h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 shrink-0">
                <CalendarBlank size={18} className="text-[#A1A1AA]" />
                <span className="text-xs sm:text-sm text-[#A1A1AA]">
                  Cadastrada em:
                </span>
              </div>
              <span className="text-xs sm:text-sm font-medium text-right">
                {new Date(data.created_at).toLocaleDateString("pt-BR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </span>
            </div>
            {data.completed_at && (
              <div className="flex items-center justify-between gap-4 border-t border-white/5 pt-3">
                <div className="flex items-center gap-2 shrink-0">
                  <CheckCircle size={18} className="text-[#00E676]" />
                  <span className="text-xs sm:text-sm text-[#A1A1AA]">
                    Finalizada em:
                  </span>
                </div>
                <span className="text-xs sm:text-sm font-medium text-right">
                  {new Date(data.completed_at).toLocaleDateString("pt-BR", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>
            )}
            {data.last_revision_at && (
              <div className="flex items-center justify-between gap-4 border-t border-white/5 pt-3">
                <div className="flex items-center gap-2 shrink-0">
                  <Wrench size={18} className="text-blue-500" />
                  <span className="text-xs sm:text-sm text-[#A1A1AA]">
                    Última Revisão:
                  </span>
                </div>
                <span className="text-xs sm:text-sm font-medium text-right">
                  {new Date(data.last_revision_at).toLocaleDateString("pt-BR", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </span>
              </div>
            )}
            {data.revision_count > 0 && (
              <div className="flex items-center justify-between gap-4 border-t border-white/5 pt-3">
                <div className="flex items-center gap-2 shrink-0">
                  <Wrench size={18} className="text-[#A1A1AA]" />
                  <span className="text-xs sm:text-sm text-[#A1A1AA]">
                    Total de Revisões:
                  </span>
                </div>
                <span className="text-xs sm:text-sm font-bold text-right">
                  {data.revision_count}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Revision History */}
        {data.revisions && data.revisions.length > 0 && (
          <div className="card p-5">
            <div className="flex items-center gap-2 mb-4">
              <Wrench size={20} className="text-blue-500 shrink-0" />
              <h3 className="font-bold text-sm sm:text-base">
                Histórico de Revisões
              </h3>
            </div>
            <div className="space-y-3">
              {data.revisions.map((rev, idx) => (
                <div
                  key={idx}
                  className="bg-[#1A1A1A] rounded-lg p-4 border border-white/5"
                  data-testid={`revision-history-${idx}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-sm font-bold">
                      Revisão #{rev.revision_number}
                    </span>
                    <span
                      className={`text-[10px] sm:text-xs font-medium px-2 py-1 rounded-md uppercase tracking-wider ${
                        rev.status === "completed"
                          ? "bg-[#00E676]/10 text-[#00E676]"
                          : rev.status === "in_progress"
                            ? "bg-yellow-500/10 text-yellow-500"
                            : "bg-[#A1A1AA]/10 text-[#A1A1AA]"
                      }`}
                    >
                      {rev.status === "completed"
                        ? "Concluída"
                        : "Em Andamento"}
                    </span>
                  </div>

                  <div className="space-y-1.5 mb-3">
                    {rev.store_name && (
                      <p className="text-xs sm:text-sm text-[#A1A1AA] truncate">
                        <span className="text-white/50">Loja:</span>{" "}
                        {rev.store_name}
                      </p>
                    )}
                    {rev.technician_name && (
                      <p className="text-xs sm:text-sm text-[#A1A1AA] truncate">
                        <span className="text-white/50">Técnico:</span>{" "}
                        {rev.technician_name}
                      </p>
                    )}
                  </div>

                  {rev.notes && (
                    <div className="bg-[#252525] p-3 rounded-lg mb-3 border border-white/5">
                      <p className="text-[11px] text-[#A1A1AA] mb-1 font-medium">
                        Observações:
                      </p>
                      <p className="text-xs sm:text-sm text-white leading-relaxed">
                        {rev.notes}
                      </p>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-4 pt-3 border-t border-white/10 mt-auto">
                    <span className="text-[11px] sm:text-xs text-[#A1A1AA]">
                      <span className="text-white/50">Início:</span>{" "}
                      {new Date(rev.started_at).toLocaleDateString("pt-BR", {
                        day: "2-digit",
                        month: "2-digit",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                    {rev.completed_at && (
                      <span className="text-[11px] sm:text-xs text-[#A1A1AA]">
                        <span className="text-white/50">Fim:</span>{" "}
                        {new Date(rev.completed_at).toLocaleDateString(
                          "pt-BR",
                          {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Observations/Issues */}
        {data.observations && data.observations.length > 0 && (
          <div className="card p-5">
            <h3 className="font-bold mb-4 text-sm sm:text-base">
              Avarias e Observações
            </h3>
            <div className="space-y-3">
              {data.observations.map((obs, idx) => {
                const config =
                  OBS_TYPE_CONFIG[obs.type] || OBS_TYPE_CONFIG.observation;
                return (
                  <div
                    key={idx}
                    className="bg-[#1A1A1A] rounded-lg p-4 border border-white/5"
                  >
                    <div className="flex flex-wrap items-center gap-2 mb-2.5">
                      <span
                        className={`text-[10px] sm:text-xs font-bold uppercase tracking-wider px-2 py-1 rounded-md ${config.color}`}
                      >
                        {config.label}
                      </span>
                      <span className="text-[10px] sm:text-xs text-[#A1A1AA]">
                        {new Date(obs.created_at).toLocaleDateString("pt-BR", {
                          day: "2-digit",
                          month: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                    <p className="text-xs sm:text-sm text-white leading-relaxed mb-2">
                      {obs.text}
                    </p>
                    <p className="text-[10px] sm:text-xs text-[#A1A1AA] pt-2 border-t border-white/10 font-medium">
                      Por: {obs.user_name}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* History Timeline */}
        {data.history && data.history.length > 0 && (
          <div className="card p-5">
            <h3 className="font-bold mb-4 text-sm sm:text-base">
              Linha do Tempo (Timeline)
            </h3>
            <div className="space-y-4 sm:space-y-6 pl-2">
              {data.history.slice(0, 15).map((action, idx) => (
                <div key={idx} className="flex gap-4 relative">
                  {/* Vertical Line */}
                  {idx !== data.history.slice(0, 15).length - 1 && (
                    <div className="absolute left-4 top-8 bottom-[-16px] sm:bottom-[-24px] w-px bg-white/10"></div>
                  )}

                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#1A1A1A] flex items-center justify-center flex-shrink-0 text-sm sm:text-base z-10 border border-white/5 shadow-sm">
                    {ACTION_ICONS[action.action_type] || "📋"}
                  </div>
                  <div className="flex-1 min-w-0 pt-1 sm:pt-2">
                    <p className="text-xs sm:text-sm font-medium leading-snug break-words">
                      {action.description}
                    </p>
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 mt-1.5">
                      <User size={12} className="text-[#A1A1AA] shrink-0" />
                      <span className="text-[10px] sm:text-xs text-[#A1A1AA] truncate">
                        {action.user_name}
                      </span>
                      <span className="text-[10px] sm:text-xs text-[#A1A1AA]">
                        •
                      </span>
                      <span className="text-[10px] sm:text-xs text-[#A1A1AA]">
                        {new Date(action.created_at).toLocaleDateString(
                          "pt-BR",
                          {
                            day: "2-digit",
                            month: "2-digit",
                            hour: "2-digit",
                            minute: "2-digit",
                          },
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center py-6">
          <p className="text-[10px] sm:text-xs text-[#A1A1AA] font-medium tracking-widest uppercase">
            EBIKE CONTROL
          </p>
        </div>
      </main>
    </div>
  );
}
